import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../../supabaseClient";

/**
 * Deduplicates players by name, keeping only the most recent season's record.
 * If two players share a name but are genuinely different people (different teams),
 * both are kept — the captain can disambiguate via the team label in the dropdown.
 */
function deduplicateByNameAndTeam(players) {
  const seen = new Map(); // key: "name|team_id" → most recent player record
  for (const p of players) {
    const key = `${p.name.toLowerCase()}|${p.team_id ?? "none"}`;
    if (!seen.has(key)) {
      seen.set(key, p); // already ordered by season_id DESC, so first = most recent
    }
  }
  return Array.from(seen.values());
}

/**
 * PlayerAutocomplete
 *
 * Drop-in replacement for the player Name <input> in Register.jsx.
 * When the captain types ≥3 characters, queries `players` for matching
 * names across all past seasons and shows a dropdown. Selecting a player
 * auto-fills name, age, jersey size, and is_muslim — but NOT jersey number,
 * since numbers commonly change between seasons.
 *
 * IMPORTANT: `value` is also mutated externally by the parent (import panel,
 * draft restore, autofill). We only want to trigger a search when a change
 * to `value` came from the user actually typing in *this* input — otherwise
 * an import would immediately reopen the "returning players" dropdown for
 * the player we just placed there, which is redundant and confusing.
 * `internalValueRef` tracks the last value *we* produced; if `value` differs
 * from it on render, the change came from outside and we skip searching.
 *
 * Props:
 *   value        {string}   Current name field value
 *   onChange     {fn}       Called with new name string on every keystroke
 *   onAutofill   {fn}       Called with { name, age, jerseySize, isMuslim }
 *                           when captain selects a suggestion
 *   placeholder  {string}
 *   className    {string}
 */
const PlayerAutocomplete = ({
  value,
  onChange,
  onAutofill,
  placeholder,
  className,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);

  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const internalValueRef = useRef(value);
  const hasMountedRef = useRef(false);

  // ── Search ────────────────────────────────────────────────────────────────

  const search = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("players")
        .select(
          "id, name, age, jersey_size, is_muslim, season_id, team_id, teams(name)",
        )
        .ilike("name", `${query}%`)
        .order("season_id", { ascending: false })
        .limit(30); // fetch more than we show to allow dedup headroom

      if (error) throw error;

      const deduped = deduplicateByNameAndTeam(data ?? []);
      setSuggestions(deduped.slice(0, 6)); // show max 6
      setOpen(deduped.length > 0);
    } catch (err) {
      console.error("[PlayerAutocomplete] search error:", err);
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Debounced trigger — skipped for externally-driven value changes ────────
  useEffect(() => {
    if (!hasMountedRef.current) {
      // First run after mount looks identical to "user typed" since
      // internalValueRef was seeded with the current value — special-case it
      // so re-expanding an already-filled player never auto-searches.
      hasMountedRef.current = true;
      return;
    }

    if (value !== internalValueRef.current) {
      internalValueRef.current = value;
      setSuggestions([]);
      setOpen(false);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value), 300);
    return () => clearTimeout(debounceRef.current);
  }, [value, search]);

  // ── Click-outside to close ────────────────────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setHighlightedIdx(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Keyboard navigation ───────────────────────────────────────────────────

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && highlightedIdx >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightedIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlightedIdx(-1);
    }
  };

  // ── Change / Selection ───────────────────────────────────────────────────

  const handleChange = (e) => {
    const v = e.target.value;
    internalValueRef.current = v; // this change came from us — allow search
    onChange(v);
  };

  const selectSuggestion = (player) => {
    internalValueRef.current = player.name; // also our own update
    onAutofill({
      name: player.name,
      age: player.age ?? "",
      jerseySize: player.jersey_size ?? "L",
      isMuslim: player.is_muslim ?? true,
      // jerseyNumber intentionally omitted — changes between seasons
    });
    setOpen(false);
    setHighlightedIdx(-1);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />

      {/* Loading indicator — subtle, inside the input row */}
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-3.5 h-3.5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Dropdown */}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden animate-fade-in">
          <li className="px-3 py-1.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide border-b dark:border-gray-700">
            Returning players
          </li>
          {suggestions.map((player, idx) => (
            <li
              key={`${player.id}`}
              onMouseDown={() => selectSuggestion(player)} // mousedown fires before blur
              onMouseEnter={() => setHighlightedIdx(idx)}
              className={`px-3 py-2.5 cursor-pointer flex items-center justify-between gap-3 transition-colors ${
                highlightedIdx === idx
                  ? "bg-orange-50 dark:bg-orange-900/30"
                  : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
              }`}
            >
              <div className="min-w-0">
                <span className="block font-medium text-gray-900 dark:text-white text-sm truncate">
                  {player.name}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                  {player.teams?.name ?? "Unknown team"}
                  {player.age ? ` · Age ${player.age}` : ""}
                  {player.jersey_size ? ` · ${player.jersey_size}` : ""}
                </span>
              </div>
              <span className="text-xs text-orange-500 dark:text-orange-400 shrink-0 font-medium">
                Autofill
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlayerAutocomplete;
